import * as signalR from '@microsoft/signalr';

import React, { PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import AuthContext from './AuthContext';
import ConfigContext from './ConfigContext';
import { NotificationSubscriptionScope, notificationHubMethods, portalNotificationEvents } from './NotificationContract';

export type FlowInstanceEventNotification = {
	topic: string;
	payload: FlowInstanceEventPayload;
	receivedAt: string;
};

export type FlowInstanceEventPayload = {
	id: string;
	tenantId: string;
	environmentId: string;
	definitionId: string;
	occurredAt: string;
	status?: string;
	currentActivityId?: string | null;
	errorMessage?: string;
	currentActivityStack?: Record<string, unknown>[];
	executionId?: string;
	runId?: string;
	parentActivityId?: string;
	name?: string;
	stepType?: string;
};

export type ReleasePlanEventNotification = {
	topic: string;
	payload: ReleasePlanEventPayload;
	receivedAt: string;
};

export type ReleasePlanEventPayload = {
	id: string;
	tenantId: string;
	targetEnvironmentId: string;
	status?: string;
	completedStepCount?: number;
	message?: string;
};

export type ConversationEventNotification = {
	topic: string;
	payload: ConversationEventPayload;
	receivedAt: string;
};

export type ConversationEventPayload = {
	id: string;
	eventType?: string;
	messageId?: string;
	status?: string;
};

export type ReactBuildEventNotification = {
	topic: string;
	payload: ReactBuildEventPayload;
	receivedAt: string;
};

export type ReactBuildEventPayload = {
	tenantId: string;
	buildId: string;
	state: string;
	updatedAt: string;
};

type NotificationSubscription = {
	scope: NotificationSubscriptionScope;
	id: string;
	count: number;
	ready: boolean;
};

const CONNECTION_RETRY_MS = 3000;
const SUBSCRIPTION_WAIT_MS = 100;
const SUBSCRIPTION_TIMEOUT_MS = 10000;
const FLOW_EVENT_BUFFER_LIMIT = 1000;

export type NotificationContextValue = {
	connection: signalR.HubConnection | null;
	connectionState: signalR.HubConnectionState;
	latestFlowInstanceEvent: FlowInstanceEventNotification | null;
	flowInstanceEvents: FlowInstanceEventNotification[];
	latestReleasePlanEvent: ReleasePlanEventNotification | null;
	releasePlanEvents: ReleasePlanEventNotification[];
	latestConversationEvent: ConversationEventNotification | null;
	latestReactBuildEvent: ReactBuildEventNotification | null;
	subscribe: (scope: NotificationSubscriptionScope, id: string) => () => void;
	subscribeToReactBuilds: () => () => void;
	ensureSubscribed: (scope: NotificationSubscriptionScope, id: string) => Promise<void>;
};

const NotificationContext = React.createContext<NotificationContextValue>({
	connection: null,
	connectionState: signalR.HubConnectionState.Disconnected,
	latestFlowInstanceEvent: null,
	flowInstanceEvents: [],
	latestReleasePlanEvent: null,
	releasePlanEvents: [],
	latestConversationEvent: null,
	latestReactBuildEvent: null,
	subscribe: () => () => undefined,
	subscribeToReactBuilds: () => () => undefined,
	ensureSubscribed: async () => {
		throw new Error('Live notifications are unavailable');
	},
});

function getHubUrl(apiUrl: string) {
	const normalizedApiUrl = apiUrl.replace(/\/$/, '');
	return normalizedApiUrl ? `${normalizedApiUrl}/hubs/notifications` : '/hubs/notifications';
}

function isStoppedDuringNegotiationError(error: unknown) {
	return error instanceof Error && error.name === 'AbortError' && error.message.includes('stopped during negotiation');
}

export const NotificationProvider: React.FC<PropsWithChildren> = ({ children }) => {
	const auth = useContext(AuthContext);
	const config = useContext(ConfigContext);
	const { authService, isAuthenticated } = auth;
	const [connection, setConnection] = useState<signalR.HubConnection | null>(null);
	const [connectionState, setConnectionState] = useState(signalR.HubConnectionState.Disconnected);
	const [flowInstanceEvents, setFlowInstanceEvents] = useState<FlowInstanceEventNotification[]>([]);
	const [releasePlanEvents, setReleasePlanEvents] = useState<ReleasePlanEventNotification[]>([]);
	const [latestConversationEvent, setLatestConversationEvent] = useState<ConversationEventNotification | null>(null);
	const [latestReactBuildEvent, setLatestReactBuildEvent] = useState<ReactBuildEventNotification | null>(null);
	const connectionRef = useRef<signalR.HubConnection | null>(null);
	const subscriptionsRef = useRef(new Map<string, NotificationSubscription>());
	const subscriptionOperationsRef = useRef(new WeakMap<signalR.HubConnection, Map<string, Promise<void>>>());

	const invokeSubscription = useCallback(
		async (hubConnection: signalR.HubConnection, action: 'subscribe' | 'unsubscribe', subscription: NotificationSubscription) => {
			let operations = subscriptionOperationsRef.current.get(hubConnection);
			if (!operations) {
				operations = new Map();
				subscriptionOperationsRef.current.set(hubConnection, operations);
			}
			const key = `${subscription.scope}:${subscription.id}`;
			// Keep joins and leaves ordered across rapid mount/unmount cycles.
			const operation = (operations.get(key) ?? Promise.resolve()).then(async () => {
				const connectionId = hubConnection.connectionId;
				try {
					if (subscription.scope === 'reactBuilds') {
						await hubConnection.invoke(notificationHubMethods.reactBuilds[action]);
					} else {
						await hubConnection.invoke(notificationHubMethods[subscription.scope][action], subscription.id);
					}
					subscription.ready =
						action === 'subscribe' &&
						subscriptionsRef.current.get(key) === subscription &&
						connectionRef.current === hubConnection &&
						hubConnection.state === signalR.HubConnectionState.Connected &&
						hubConnection.connectionId === connectionId;
				} catch (error) {
					subscription.ready = false;
					console.error(`Notification ${action} failed for ${subscription.scope}:${subscription.id}:`, error);
				}
			});
			operations.set(key, operation);
			await operation;
			if (operations.get(key) === operation) {
				operations.delete(key);
			}
		},
		[],
	);

	const replaySubscriptions = useCallback(
		async (hubConnection: signalR.HubConnection) => {
			await Promise.all(
				Array.from(subscriptionsRef.current.values())
					.filter((subscription) => !subscription.ready)
					.map((subscription) => invokeSubscription(hubConnection, 'subscribe', subscription)),
			);
		},
		[invokeSubscription],
	);

	const subscribe = useCallback(
		(scope: NotificationSubscriptionScope, id: string) => {
			const key = `${scope}:${id}`;
			const existing = subscriptionsRef.current.get(key);
			if (existing) {
				existing.count += 1;
			} else {
				const subscription = { scope, id, count: 1, ready: false };
				subscriptionsRef.current.set(key, subscription);
				const currentConnection = connectionRef.current;
				if (currentConnection?.state === signalR.HubConnectionState.Connected) {
					void invokeSubscription(currentConnection, 'subscribe', subscription);
				}
			}

			let released = false;
			return () => {
				if (released) {
					return;
				}

				released = true;
				const active = subscriptionsRef.current.get(key);
				if (!active) {
					return;
				}

				active.count -= 1;
				if (active.count > 0) {
					return;
				}

				subscriptionsRef.current.delete(key);
				const currentConnection = connectionRef.current;
				if (currentConnection?.state === signalR.HubConnectionState.Connected) {
					void invokeSubscription(currentConnection, 'unsubscribe', active);
				}
			};
		},
		[invokeSubscription],
	);
	const subscribeToReactBuilds = useCallback(() => subscribe('reactBuilds', 'tenant'), [subscribe]);
	const ensureSubscribed = useCallback(
		async (scope: NotificationSubscriptionScope, id: string) => {
			const release = subscribe(scope, id);
			try {
				const deadline = Date.now() + SUBSCRIPTION_TIMEOUT_MS;
				while (Date.now() < deadline) {
					if (connectionRef.current?.state === signalR.HubConnectionState.Connected && subscriptionsRef.current.get(`${scope}:${id}`)?.ready) {
						return;
					}
					await new Promise((resolve) => setTimeout(resolve, SUBSCRIPTION_WAIT_MS));
				}
				throw new Error('Live progress is not connected yet. Please try again.');
			} finally {
				release();
			}
		},
		[subscribe],
	);

	useEffect(() => {
		if (!isAuthenticated) {
			setConnection(null);
			setConnectionState(signalR.HubConnectionState.Disconnected);
			setFlowInstanceEvents([]);
			setReleasePlanEvents([]);
			setLatestConversationEvent(null);
			setLatestReactBuildEvent(null);
			return;
		}

		let isDisposed = false;
		let recovering = false;
		const resetSubscriptions = () =>
			subscriptionsRef.current.forEach((subscription) => {
				subscription.ready = false;
			});
		resetSubscriptions();
		const hubConnection = new signalR.HubConnectionBuilder()
			.withUrl(getHubUrl(config.API_URL), {
				accessTokenFactory: async () => (await authService.getAccessToken()) ?? '',
			})
			.withAutomaticReconnect()
			.configureLogging(signalR.LogLevel.Warning)
			.build();
		connectionRef.current = hubConnection;

		const updateState = () => {
			if (!isDisposed) {
				setConnectionState(hubConnection.state);
			}
		};

		hubConnection.on(portalNotificationEvents.flowInstanceEvent, (event: FlowInstanceEventNotification) => {
			setFlowInstanceEvents((current) => [event, ...current].slice(0, FLOW_EVENT_BUFFER_LIMIT));
		});

		hubConnection.on(portalNotificationEvents.releasePlanEvent, (event: ReleasePlanEventNotification) => {
			setReleasePlanEvents((current) => [event, ...current].slice(0, 100));
		});

		hubConnection.on(portalNotificationEvents.conversationEvent, (event: ConversationEventNotification) => {
			setLatestConversationEvent(event);
		});

		hubConnection.on(portalNotificationEvents.reactBuildEvent, (event: ReactBuildEventNotification) => {
			setLatestReactBuildEvent(event);
		});

		hubConnection.onreconnecting(() => {
			resetSubscriptions();
			updateState();
		});
		hubConnection.onreconnected(() => {
			resetSubscriptions();
			updateState();
			void replaySubscriptions(hubConnection);
		});
		hubConnection.onclose(() => {
			resetSubscriptions();
			updateState();
		});

		setConnection(hubConnection);
		setConnectionState(hubConnection.state);

		const recover = async () => {
			if (isDisposed || recovering) {
				return;
			}
			recovering = true;
			try {
				if (hubConnection.state === signalR.HubConnectionState.Disconnected) {
					await hubConnection.start();
				}
				if (isDisposed) {
					return;
				}
				updateState();
				if (hubConnection.state === signalR.HubConnectionState.Connected) {
					await replaySubscriptions(hubConnection);
				}
			} catch (error) {
				if (isDisposed && isStoppedDuringNegotiationError(error)) {
					return;
				}

				console.error('Notification connection failed:', error);
				updateState();
			} finally {
				recovering = false;
			}
		};
		void recover();
		const recoveryTimer = setInterval(() => {
			void recover();
		}, CONNECTION_RETRY_MS);

		return () => {
			isDisposed = true;
			clearInterval(recoveryTimer);
			if (connectionRef.current === hubConnection) {
				connectionRef.current = null;
			}
			setConnection(null);
			setConnectionState(signalR.HubConnectionState.Disconnected);
			void hubConnection.stop();
		};
	}, [authService, config.API_URL, isAuthenticated, replaySubscriptions]);

	const value = useMemo<NotificationContextValue>(
		() => ({
			connection,
			connectionState,
			latestFlowInstanceEvent: flowInstanceEvents[0] ?? null,
			flowInstanceEvents,
			latestReleasePlanEvent: releasePlanEvents[0] ?? null,
			releasePlanEvents,
			latestConversationEvent,
			latestReactBuildEvent,
			subscribe,
			subscribeToReactBuilds,
			ensureSubscribed,
		}),
		[
			connection,
			connectionState,
			flowInstanceEvents,
			latestConversationEvent,
			latestReactBuildEvent,
			releasePlanEvents,
			subscribe,
			subscribeToReactBuilds,
			ensureSubscribed,
		],
	);

	return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export const useNotifications = () => useContext(NotificationContext);

export default NotificationContext;
